<template>
  <div :class="[$style.item, isActive && $style.active]">
    <div 
      v-if="plan.name === 'Team'"
      :class="$style.chip"
    >
      Most Popular
    </div>
    
    <div :class="$style.planName">
      <div :class="$style.title">
        {{ currentPlan.name }}
      </div>
      <div :class="$style.caption">
        {{ planDescription }}
      </div>
    </div>
    <div :class="$style.planPrice">
      <div :class="$style.amount">
        <span :class="$style.currency">$</span>
        <span :class="$style.quantity">{{ pricePerMonth }}</span>
      </div>
      <div :class="$style.description">
        {{ 
          currentPlan.name === 'Free' 
            ? t('prices.list.period.free') 
            : Number(activeTimeLine) < 3 
            ? t('prices.list.period.monthly')
            : t('prices.list.period.timeline', { period: activeTimeLine }) 
        }}
      </div>
      <UiButton 
        :class="$style.action"
        type="small"
        @click.prevent="emits('action', currentPlan._id)"
      >
        <template #label>
        {{ !props.user ? t('prices.buttons.create') : t('prices.buttons.plan')}}
           
        </template>
      </UiButton>
    </div>
    <div 
      :class="$style.features"
    >
    <div
      v-for="([value, item], index) in filteredFeatures"
      :key="index"
    >
      <div :class="$style.feature">
        <UiIconsCheckCircle />
        <div>{{ featureLabel(value) }}</div>
        <div>
          {{ item }}
          <div 
            v-if="isStarter && value === 'profiles'"
            :class="$style.changer"
          >
            <div 
              @click="prevPlan"
            >
              <UiIconsMinusIcon />
            </div>
            <div 
              @click="nextPlan" 
            >
              <UiIconsPlusIcon />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Decimal } from 'decimal.js';
import { ref, computed } from 'vue';
import type { SubscrbPlanItemParams } from './index.js';
import { getCloudPlanFeatures, isCloudPlan } from '~/utils/planFeatures';

const { t, te } = useI18n();

const props = defineProps<SubscrbPlanItemParams>();

const emits = defineEmits(['action']);

const starterNames = ['Starter-1', 'Starter-2', 'Starter-3'];
const isStarter = computed(() => starterNames.includes(props.plan.name));
const allPlans = Array.isArray(props.allPlans) ? props.allPlans : [];
const switchPlans = isStarter.value && allPlans.length
  ? allPlans.filter(item => starterNames.includes(item.name))
  : [props.plan];
const starterIdx = switchPlans.findIndex(p => p.name === 'Starter-1');
const [minIdx, maxIdx] = [0, switchPlans.length - 1];
const selectedIdx = ref(starterIdx !== -1 ? starterIdx : 0);

const currentPlan = computed(() => {
  if (isStarter.value) {
    return switchPlans[selectedIdx.value] || props.plan;
  }
  return props.plan;
});

const planDescription = computed(() => {
  if (currentPlan.value.isCustom) return '';
  const key = `prices.list.description.${currentPlan.value.name}`;
  return te(key) ? t(key) : '';
});

const filteredFeatures = computed(() => {
  const features = currentPlan.value.features || {};

  if (isCloudPlan(currentPlan.value)) {
    return [
      ...getCloudPlanFeatures(features as Record<string, unknown>)
        .filter(([, value]) => typeof value !== 'boolean')
        .slice(0, 2),
      ['usageRate', t('prices.list.usageRate')],
    ];
  }

  return Object.entries(features).filter(
    ([item]) => item === 'profiles' || item === 'proxies',
  );
});

const featureLabel = (key: string) => {
  if (key === 'usageRate') return t('prices.params.usageRate');
  const i18nKey = `subscription.params.${key}`;
  return te(i18nKey) ? t(i18nKey) : key;
};

const activeTimeLine = inject<globalThis.Ref<'1' | '3' | '6' | '12'>>('activeTimeLine');
const pricePerMonth = computed(() => {
  if (currentPlan.value.name === 'Custom') return '';
  const timeLineValue = activeTimeLine?.value || '1';

  if (currentPlan.value.name === 'Free') {
    return 0;
  }

  const prices = currentPlan.value.finalPrices;
  if (!prices || !prices[timeLineValue]) return '';
  if (Number(timeLineValue) > 1) {
    return new Decimal(prices[timeLineValue]).div(Number(timeLineValue)).toFixed(2).toString() //prices[timeLineValue]
  }
  return prices[timeLineValue].toString();
});

function prevPlan() {
  if (selectedIdx.value > minIdx) selectedIdx.value--;
}
function nextPlan() {
  if (selectedIdx.value < maxIdx) selectedIdx.value++;
}
</script>

<style lang="scss" module>
  .item {
    position: relative;
    display: flex;
    padding: 20px 16px;
    flex-direction: column;
    justify-content: flex-end;
    gap: 24px;
    flex: 1 1 0;
    // align-self: stretch;
    border-radius: 8px;
    border: 1px solid #EAEAEA;

    * {
      transition: 200ms ease;
    }

    // &.active {
    //   background-color: #b3baff; // #293EFF
    // }
  }

  .chip {
    display: flex;
    padding: 2px 8px;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 4px;
    right: 4px;
    border-radius: 6px;
    background: rgba(41, 62, 255, 0.10);
    color: #293EFF;
  }

  .params {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .plan {
    &Name {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: auto;

      .title {
        font-size: 24px;
        color: #121E29;
        font-weight: 700;
      }

      .caption {
        color: #596269;
        font-size: 14px;
        line-height: 1.4;
      }
    }

    &Price {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      .amount {
        display: flex;
        align-items: flex-start;
        gap: 15px;
        color: #121E29;
        font-weight: 700;
        line-height: 1;

        :global(.dark) & {
          color: #e6edf3;
        }

        .currency {
          font-size: 20px;
        }

        .quantity {
          font-size: 36px;

          @media (max-width: 767px) {
            font-size: 28px;
          }
        }
      }

      .description {
        font-size: 14px;
        font-weight: 500;
        color: #9A9FA9;
        min-height: 60px;

        @media (max-width: 767px) {
          font-size: 12px;
          min-height: 36px;
        }
      }
    }
  }

  .action {
    // margin: 8px 0 24px 0;
  }

  .features {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .feature {
      display: flex;
      align-items: center; 
      gap: 8px;
      color: #121E29;
      font-size: 14px;

      svg {
        width: 16px;
        height: 16px;
      }

      div:last-child {
        display: flex;
        align-items: center;
        gap: 5px;
        margin-left: auto;
      }

      .changer {
        display: inline-flex;
        gap: 5px;

        div {
          height: 16px;

          svg {
            color: #596269;
            transition: 300ms ease;

            &:hover {
              color: #293EFF;
              cursor: pointer;
            }
          }
        }
      }
    }

  }

</style>